import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';

import { PlusOutlined, CloseOutlined, LinkOutlined } from '@ant-design/icons';
import {
  Tabs,
  Typography,
  Button,
  message,
  Drawer
} from 'antd';

import { ExecutionHistoryTable } from './components/history/ExecutionHistoryTable';
import { RuleFormModal } from './components/rules/RuleFormModal';
import { RulesTable } from './components/rules/RulesTable';
import { FormState, RuleTableItem, ExecutionHistoryItem, RuleBlock, CampaignRule, CampaignRulesListRequest, CampaignRuleCreateRequest, HierarchicalActionTypes, DictionariesResponse, ActionsHistoryRequest, ActionsHistoryItem } from './types';
import { uid } from './utils/helpers';
import { migrateState } from './utils/stateManagement';
import { adsRulesApi } from '../../shared/common/api/endpoints/adsRulesApi';
import MainLayout from '../../shared/common/components/layouts/MainLayout';
import { useTablePagination } from '../../shared/common/hooks/useTablePagination';
import { useTableSorting, convertAntdSorterToApi } from '../../shared/common/hooks/useTableSorting';


const { Title, Text } = Typography;

function AdsRules() {
  const [apiRules, setApiRules] = useState<CampaignRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [sorting, setSorting] = useState<{
    sortField: CampaignRulesListRequest['sortField'];
    sortDirection: CampaignRulesListRequest['sortDirection'];
  }>({
    sortField: 'created_date',
    sortDirection: 'desc'
  });
  const [actionTypes, setActionTypes] = useState<HierarchicalActionTypes | null>(null);
  const [dictionaries, setDictionaries] = useState<DictionariesResponse['data'] | null>(null);
  const [apiHistory, setApiHistory] = useState<ActionsHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({
    current_page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0
  });
  const [savedRules, setSavedRules] = useState<RuleTableItem[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RuleTableItem | undefined>();
  const [executionHistory, setExecutionHistory] = useState<ExecutionHistoryItem[]>([]);

  const [state, setState] = useState<FormState>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('conditions_form_v2_antd') : null;
    if (saved) {
      try {
        return migrateState(JSON.parse(saved));
      } catch (error) {
        console.error(error);
      }
    }
    return {
      adSource: 'yandex_direct',
      accounts: [],
      campaignIds: [],
      adGroupIds: [],
      adIds: [],
      scope: ['campaigns'],
      lookbackDays: 5,
      checkEveryDays: 5,
      rules: [
        {
          id: uid(),
          name: 'Условие 1',
          periodDays: 5,
          groups: [
            {
              id: uid(),
              logic: 'OR',
              periodDays: 5,
              atoms: [
                { id: uid(), metric: 'users_count', comparator: '>', value: 1000, periodDays: 5 },
                { id: uid(), metric: 'user_price', comparator: '<', value: 200, periodDays: 5 },
              ],
            },
          ],
          actions: {
            budgetAction: 'none',
            budgetAmount: 1,
            budgetUnit: 'rub',
            sendTelegram: false,
            tgMessage: '',
            tgRecipients: []
          },
        },
      ],
    } as FormState;
  });

  const [activeTab, setActiveTab] = useState<string>('rules-active');
  const [helpDrawerVisible, setHelpDrawerVisible] = useState(false);
  const iframeRef = useRef(null);

  const fetchRules = useCallback(async (isArchived = false, page = 1, pageSize = 20, filters?: CampaignRulesListRequest, sortField?: CampaignRulesListRequest['sortField'], sortDirection?: CampaignRulesListRequest['sortDirection']) => {
    setLoading(true);
    try {
      const params: CampaignRulesListRequest = {
        is_archieved: isArchived,
        page,
        pageSize,
        sortField: sortField || sorting.sortField,
        sortDirection: sortDirection || sorting.sortDirection,
        ...filters, // Добавляем фильтры если они переданы
      };

      const response = await adsRulesApi.fetchRulesList(params);

      if (response.data.success) {
        setApiRules(response.data.data);
        setPagination(response.data.pagination);
      } else {
        message.error('Ошибка при загрузке правил: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
      message.error('Ошибка при загрузке правил');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActionTypes = async () => {
    try {
      const response = await adsRulesApi.fetchDictionaries({ dictionaries: 'action_types' });
      if (response.data.success && response.data.data.action_types) {
        setActionTypes(response.data.data.action_types);
      }
    } catch (error) {
      console.error('Error fetching action types:', error);
    }
  };

  const fetchDictionariesForFilters = async () => {
    try {
      const response = await adsRulesApi.fetchDictionaries({
        dictionaries: 'rule_names,ad_accounts,action_types,employees'
      });
      if (response.data.success) {
        setDictionaries(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dictionaries for filters:', error);
    }
  };

  const fetchHistory = useCallback(async (filters?: ActionsHistoryRequest, page = 1, pageSize = 20) => {
    setHistoryLoading(true);
    try {
      const params: ActionsHistoryRequest = {
        page,
        pageSize,
        ...filters,
      };

      const response = await adsRulesApi.fetchActionsHistory(params);

      if (response.data.success) {
        setApiHistory(response.data.data);
        setHistoryPagination(response.data.pagination);
      } else {
        message.error('Ошибка при загрузке истории: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      message.error('Ошибка при загрузке истории');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('conditions_form_v2_antd', JSON.stringify(state));
    }
  }, [state]);

  useEffect(() => {
    if (activeTab === 'rules-active') {
      fetchRules(false);
    } else if (activeTab === 'rules-archived') {
      fetchRules(true);
    } else if (activeTab === 'history') {
      fetchHistory();
    }
    fetchDictionariesForFilters();
    // eslint-disable-next-line
  }, [activeTab]);

  useEffect(() => {
    fetchActionTypes();
    fetchDictionariesForFilters();
  }, []);

  const scopeOne = state.scope[0] || 'campaigns';

  const handleFiltersChange = useCallback((filters: CampaignRulesListRequest) => {
    fetchRules(activeTab === 'rules-archived', 1, 20, filters, sorting.sortField, sorting.sortDirection);
    // eslint-disable-next-line
  }, [activeTab, sorting]);

  const handleHistoryFiltersChange = useCallback((filters: ActionsHistoryRequest) => {
    fetchHistory(filters, 1, 20);
    // eslint-disable-next-line
  }, []);


  const handleTableChange = useCallback((paginationInfo: { current?: number; pageSize?: number }, filters: unknown, sorter: { field?: string; order?: 'ascend' | 'descend' | null }) => {
    const { current = pagination.page, pageSize = pagination.pageSize } = paginationInfo;
    const { field, order } = sorter;

    // Используем утилиту для конвертации сортировки
    const apiSort = convertAntdSorterToApi({ field, order });
    const sortField = Object.keys(apiSort)[0] as CampaignRulesListRequest['sortField'] | undefined;
    const sortDirection = sortField ? (apiSort[sortField] === 'ASC' ? 'asc' : 'desc') as CampaignRulesListRequest['sortDirection'] : undefined;

    // Update sorting state only if sorting changed
    if (sortField && sortDirection) {
      setSorting({ sortField, sortDirection });
      fetchRules(activeTab === 'rules-archived', current, pageSize, undefined, sortField, sortDirection);
    } else if (field === undefined && order === undefined) {
      // No sorting change, just pagination - preserve current sorting
      fetchRules(activeTab === 'rules-archived', current, pageSize, undefined, sorting.sortField, sorting.sortDirection);
    } else {
      // Reset to default sorting (when clicking same column third time)
      setSorting({ sortField: 'created_date', sortDirection: 'desc' });
      fetchRules(activeTab === 'rules-archived', current, pageSize, undefined, 'created_date', 'desc');
    }
  }, [activeTab, sorting, pagination.page, pagination.pageSize, fetchRules]);

  const convertToApiFormat = (rule: RuleBlock, formData: Partial<FormState>): CampaignRuleCreateRequest => {
    const sourceId = formData.adSource === 'yandex_direct' ? 2 : 9;

    const objectTypeMap = {
      'campaigns': 1,
      'adGroups': 2,
      'ads': 3
    } as const;
    const objectTypeId = objectTypeMap[formData.scope?.[0] || 'campaigns'] as 1 | 2 | 3;

    let actionTypeId: number;
    if (rule.actions.actionType && !isNaN(Number(rule.actions.actionType))) {
      actionTypeId = parseInt(rule.actions.actionType.toString());
    } else {
      const actionTypeMap = {
        'none': 1,
        'increase': 2,
        'decrease': 3,
        'disable': 4
      } as const;
      actionTypeId = actionTypeMap[rule.actions.budgetAction || 'none'] as 1 | 2 | 3 | 4;
    }

    const actionUnitId = rule.actions.budgetUnit === 'rub' ? 1 : 2;

    // Валидация threshold_value перед созданием condition_blocks
    // for (const group of rule.groups) {
    //   for (const atom of group.atoms) {
    //     if (Number(atom.value) === 0) {
    //       const periodDays = atom.periodDays ?? rule.periodDays;
    //       throw new Error(`Значение за последние ${periodDays} дней не может быть 0`);
    //     }
    //   }
    // }


      for (const group of rule.groups) {
      for (const atom of group.atoms) {
        const value = Number(atom.value);

        if (atom.value === '' || Number.isNaN(value)) {
          const periodDays = atom.periodDays ?? rule.periodDays;
          throw new Error(`Укажите значение за последние ${periodDays} дней`);
        }
      }
    }

    const conditionBlocks = rule.groups.map(group => ({
      conditions: group.atoms.map(atom => {
        const start = atom.periodDays ?? rule.periodDays;
        const end = typeof atom.periodDaysUntil === 'number' ? atom.periodDaysUntil : undefined;

        const cond: any = {
          metric_name: atom.metric,
          comparison_operator: atom.comparator,
          threshold_value: Number(atom.value)
        };

        cond.period_days_start = start;
        cond.period_days_end = (typeof end === 'number' && end > 0) ? end : 0;

        return cond;
      })
    }));

    const adAccounts = (formData.accounts || []).map(id => {
      if (typeof id === 'number') return id;
      if (typeof id === 'string' && id.startsWith('acc-')) {
        return parseInt(id.replace('acc-', ''));
      }
      return parseInt(id);
    });

    const adCampaigns = (formData.campaignIds || []).map(id => {
      if (typeof id === 'number') return id;
      if (typeof id === 'string' && id.startsWith('c-')) {
        return parseInt(id.replace('c-', ''));
      }
      return parseInt(id);
    });

    const adGroups = (formData.adGroupIds || []).map(id => {
      if (typeof id === 'number') return id;
      if (typeof id === 'string' && id.startsWith('g-')) {
        return parseInt(id.replace('g-', ''));
      }
      return parseInt(id);
    });

    const ads = (formData.adIds || []).map(id => {
      if (typeof id === 'number') return id;
      if (typeof id === 'string' && id.startsWith('a-')) {
        return parseInt(id.replace('a-', ''));
      }
      return parseInt(id);
    });

    const telegramUsers = rule.actions.sendTelegram && rule.actions.tgRecipients
      ? rule.actions.tgRecipients.map(user => parseInt(user.id))
      : null;

    return {
      rule_name: rule.name,
      source_id: sourceId,
      ad_accounts: adAccounts,
      ad_campaigns: adCampaigns,
      ad_groups: adGroups,
      ads: ads,
      object_type_id: objectTypeId,
      criterion_text: rule.groups.map(group =>
        group.atoms.map(atom =>
          `${atom.metric} ${atom.comparator} ${atom.value} за последние ${atom.periodDays ?? rule.periodDays} дней`
        ).join(' ИЛИ ')
      ).join(' И '),
      action_type_id: actionTypeId as 1 | 2 | 3 | 4,
      action_amount: (actionTypeId === 2 || actionTypeId === 3 || (actionTypeId >= 21 && actionTypeId <= 35)) ? rule.actions.budgetAmount : null,
      action_unit_id: (actionTypeId === 2 || actionTypeId === 3 || (actionTypeId >= 21 && actionTypeId <= 35)) ? actionUnitId : null,
      send_telegram: rule.actions.sendTelegram,
      telegram_message: rule.actions.sendTelegram ? rule.actions.tgMessage : null,
      telegram_users: telegramUsers,
      // Используем поле из общей формы (Частота срабатывания правил)
      frequency_days: formData.checkEveryDays ?? rule.periodDays,
      condition_blocks: conditionBlocks
    };
  };

  const handleAddRule = () => {
    setEditingRule(undefined);
    setModalOpen(true);
  };

  const handleEditRule = (rule: RuleTableItem) => {
    setEditingRule(rule);
    setModalOpen(true);
  };

  const handleEditApiRule = async (ruleId: number) => {
    try {
      setLoading(true);
      const response = await adsRulesApi.getRule(ruleId);

      if (response.data.success) {
        const apiRule = response.data.data;

        const convertedRule: RuleTableItem = {
          id: apiRule.id.toString(),
          name: apiRule.rule_name,
          periodDays: apiRule.frequency_days,
          groups: apiRule.condition_blocks.map((block, blockIndex) => ({
            id: `group-${blockIndex}`,
            logic: 'OR' as const,
            atoms: block.conditions.map((cond, condIndex) => {
              const start = (cond as any).period_days_start ?? (cond as any).period_days ?? apiRule.frequency_days;
              const end = (cond as any).period_days_end ?? 0;
              return ({
                id: `atom-${blockIndex}-${condIndex}`,
                metric: cond.metric_name as any,
                comparator: cond.comparison_operator as any,
                value: cond.threshold_value,
                periodDays: start,
                periodDaysUntil: end > 0 ? end : undefined
              });
            })
          })),
          actions: {
            budgetAction: apiRule.action_type_id === 1 ? 'none' :
              apiRule.action_type_id === 2 ? 'increase' :
                apiRule.action_type_id === 3 ? 'decrease' :
                  apiRule.action_type_id === 4 ? 'disable' : 'none',
            budgetAmount: apiRule.action_amount || 0,
            budgetUnit: apiRule.action_unit_id === 1 ? 'rub' : 'percent',
            sendTelegram: apiRule.send_telegram,
            tgMessage: apiRule.telegram_message || '',
            tgRecipients: apiRule.telegram_users.map(user => ({
              id: user.id.toString(),
              name: user.name,
              username: ''
            })),
            actionType: apiRule.action_type_id.toString()
          },
          isActive: false,
          isArchived: false,
          createdAt: '',
          createdBy: '',
          lastChecked: undefined,
          formData: {
            adSource: apiRule.source_id === 2 ? 'yandex_direct' : 'vk_ads',
            accounts: apiRule.ad_accounts.map(acc => acc.id.toString()),
            campaignIds: apiRule.ad_campaigns.map(camp => camp.id.toString()),
            // Сохраняем варианты кампаний с именами, чтобы корректно отображать метки в селекте
            campaignOptions: apiRule.ad_campaigns.map(camp => ({ id: camp.id.toString(), name: camp.name })),
            adGroupIds: apiRule.ad_groups.map(group => group.id.toString()),
            adGroupOptions: apiRule.ad_groups.map(group => ({ id: group.id.toString(), name: group.name })),
            adIds: apiRule.ads.map(ad => ad.id.toString()),
            adOptions: apiRule.ads.map(ad => ({ id: ad.id.toString(), name: ad.name })),
            scope: [apiRule.object_type_id === 1 ? 'campaigns' :
              apiRule.object_type_id === 2 ? 'adGroups' :
                apiRule.object_type_id === 3 ? 'ads' : 'campaigns'],
            // частота для инпута "Каждые N дней"
            checkEveryDays: apiRule.frequency_days
          }
        };

        setEditingRule(convertedRule);
        setModalOpen(true);
      } else {
        message.error('Ошибка при загрузке правила: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('Error fetching rule:', error);

      if (error.response?.status === 400) {
        message.error('Неверный ID правила');
      } else if (error.response?.status === 401) {
        message.error('Ошибка авторизации');
      } else if (error.response?.status === 403) {
        message.error('Недостаточно прав для просмотра правила');
      } else if (error.response?.status === 404) {
        message.error('Правило не найдено');
      } else {
        message.error('Ошибка при загрузке правила');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateRule = (rule: RuleTableItem) => {
    const duplicatedRule = {
      ...rule,
      id: uid(),
      name: `${rule.name} (копия)`,
      groups: rule.groups.map(group => ({
        ...group,
        id: uid(),
        atoms: group.atoms.map(atom => ({
          ...atom,
          id: uid()
        }))
      }))
    };

    setEditingRule({ ...duplicatedRule, isDuplicate: true } as any);
    setModalOpen(true);
  };

  const handleDuplicateApiRule = async (ruleId: number) => {
    try {
      setLoading(true);
      const response = await adsRulesApi.getRule(ruleId);

      if (response.data.success) {
        const apiRule = response.data.data;

        const duplicatedRule: RuleTableItem = {
          id: uid(),
          name: `${apiRule.rule_name} (копия)`,
          periodDays: apiRule.frequency_days,
          groups: apiRule.condition_blocks.map((block, blockIndex) => ({
            id: uid(),
            logic: 'OR' as const,
            atoms: block.conditions.map((cond, condIndex) => {
              const start = (cond as any).period_days_start ?? (cond as any).period_days ?? apiRule.frequency_days;
              const end = (cond as any).period_days_end ?? 0;
              return ({
                id: uid(),
                metric: cond.metric_name as any,
                comparator: cond.comparison_operator as any,
                value: cond.threshold_value,
                periodDays: start,
                periodDaysUntil: end > 0 ? end : undefined
              });
            })
          })),
          actions: {
            budgetAction: apiRule.action_type_id === 1 ? 'none' :
              apiRule.action_type_id === 2 ? 'increase' :
                apiRule.action_type_id === 3 ? 'decrease' :
                  apiRule.action_type_id === 4 ? 'disable' : 'none',
            budgetAmount: apiRule.action_amount || 0,
            budgetUnit: apiRule.action_unit_id === 1 ? 'rub' : 'percent',
            sendTelegram: apiRule.send_telegram,
            tgMessage: apiRule.telegram_message || '',
            tgRecipients: apiRule.telegram_users.map(user => ({
              id: user.id.toString(),
              name: user.name,
              username: ''
            })),
            actionType: apiRule.action_type_id.toString()
          },
          isActive: false,
          isArchived: false,
          createdAt: '',
          createdBy: '',
          lastChecked: undefined,
          formData: {
            adSource: apiRule.source_id === 2 ? 'yandex_direct' : 'vk_ads',
            accounts: apiRule.ad_accounts.map(acc => acc.id.toString()),
            campaignIds: apiRule.ad_campaigns.map(camp => camp.id.toString()),
            campaignOptions: apiRule.ad_campaigns.map(camp => ({ id: camp.id.toString(), name: camp.name })),
            adGroupIds: apiRule.ad_groups.map(group => group.id.toString()),
            adGroupOptions: apiRule.ad_groups.map(group => ({ id: group.id.toString(), name: group.name })),
            adIds: apiRule.ads.map(ad => ad.id.toString()),
            adOptions: apiRule.ads.map(ad => ({ id: ad.id.toString(), name: ad.name })),
            scope: [apiRule.object_type_id === 1 ? 'campaigns' :
              apiRule.object_type_id === 2 ? 'adGroups' :
                apiRule.object_type_id === 3 ? 'ads' : 'campaigns'],
            checkEveryDays: apiRule.frequency_days
          }
        };

        setEditingRule({ ...duplicatedRule, isDuplicate: true } as any);
        setModalOpen(true);
      } else {
        message.error('Ошибка при загрузке правила: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('Error fetching rule for duplication:', error);

      if (error.response?.status === 400) {
        message.error('Неверный ID правила');
      } else if (error.response?.status === 401) {
        message.error('Ошибка авторизации');
      } else if (error.response?.status === 403) {
        message.error('Недостаточно прав для просмотра правила');
      } else if (error.response?.status === 404) {
        message.error('Правило не найдено');
      } else {
        message.error('Ошибка при загрузке правила');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRule = async (rule: RuleBlock, formData: Partial<FormState>) => {
    const now = new Date().toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    if (editingRule && !(editingRule as any).isDuplicate) {
      try {
        setLoading(true);
        const apiData = convertToApiFormat(rule, formData);
        const ruleId = parseInt(editingRule.id);

        const response = await adsRulesApi.updateRule(ruleId, apiData);

        if (response.data.success) {
          message.success('Правило успешно обновлено');

          fetchRules(activeTab === 'rules-archived');
          // Refresh dictionaries to update filter options with new rule data
          fetchDictionariesForFilters();
          setModalOpen(false);
        } else {
          message.error('Ошибка при обновлении правила: ' + response.data.message);
        }
      } catch (error: any) {
        console.error('Error updating rule:', error);

        // Проверяем, является ли ошибка валидацией threshold_value
        if (error.message && error.message.includes('Значение за последние')) {
          message.error(error.message);
        } else if (error.response?.status === 400) {
          message.error('Неверный формат данных. Проверьте заполнение всех полей.');
        } else if (error.response?.status === 401) {
          message.error('Ошибка авторизации.');
        } else if (error.response?.status === 403) {
          message.error('Недостаточно прав для редактирования правила.');
        } else if (error.response?.status === 404) {
          message.error('Правило не найдено.');
        } else if (error.response?.status === 422) {
          const errorMessage = error.response?.data?.message || 'Ошибка валидации данных';
          message.error(errorMessage);
        } else {
          message.error('Произошла ошибка при обновлении правила.');
        }
      } finally {
        setLoading(false);
      }
    } else {
      try {
        setLoading(true);
        const apiData = convertToApiFormat(rule, formData);

        const response = await adsRulesApi.createRule(apiData);

        if (response.data.success) {
          message.success('Правило успешно создано');

          const newRule: RuleTableItem = {
            ...rule,
            id: response.data.data.rule_id.toString(),
            isActive: false,
            createdAt: now,
            createdBy: 'Текущий пользователь',
            lastChecked: undefined,
            formData,
          };
          setSavedRules(prev => [...prev, newRule]);

          if (activeTab === 'rules-active') {
            fetchRules(false);
          }

          // Refresh dictionaries to update filter options with new rule data
          fetchDictionariesForFilters();
          setModalOpen(false);
        } else {
          message.error('Ошибка при создании правила: ' + response.data.message);
        }
      } catch (error: any) {
        console.error('Error creating rule:', error);

        // Проверяем, является ли ошибка валидацией threshold_value
        if (error.message && error.message.includes('Значение за последние')) {
          message.error(error.message);
        } else if (error.response?.status === 400) {
          message.error('Неверный формат данных. Проверьте заполнение всех обязательных полей.');
        } else if (error.response?.status === 401) {
          message.error('Ошибка авторизации. Пожалуйста, войдите в систему заново.');
        } else if (error.response?.status === 403) {
          message.error('Недостаточно прав для создания правила.');
        } else if (error.response?.status === 422) {
          const errorMessage = error.response?.data?.message || 'Ошибка валидации данных';
          message.error(errorMessage);
        } else {
          message.error('Произошла ошибка при создании правила. Попробуйте позже.');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteRule = (id: string) => {
    setSavedRules(prev => prev.map(r =>
      r.id === id ? { ...r, isArchived: true, isActive: false } : r
    ));
    message.success('Правило перенесено в архив');
  };

  const handleUnarchiveRule = (id: string) => {
    setSavedRules(prev => prev.map(r =>
      r.id === id ? { ...r, isArchived: false } : r
    ));
    message.success('Правило восстановлено из архива');
  };

  const handleToggleStatus = (id: string, isActive: boolean) => {
    setSavedRules(prev => prev.map(r =>
      r.id === id ? { ...r, isActive } : r
    ));
    message.success(isActive ? 'Правило включено' : 'Правило отключено');
  };

  const handleApiStatusToggle = async (ruleId: number, isActive: boolean) => {
    try {
      setLoading(true);
      const statusId = isActive ? 1 : 2;

      await adsRulesApi.updateRuleStatus(ruleId, { status_id: statusId });

      message.success(isActive ? 'Правило активировано' : 'Правило деактивировано');

      fetchRules(activeTab === 'rules-archived');
    } catch (error: any) {
      console.error('Error updating rule status:', error);
      if (error.response?.status === 400) {
        message.error('Неверный ID правила или статус');
      } else if (error.response?.status === 401) {
        message.error('Ошибка авторизации');
      } else if (error.response?.status === 403) {
        message.error('Недостаточно прав для изменения статуса');
      } else if (error.response?.status === 404) {
        message.error('Правило не найдено');
      } else {
        message.error('Ошибка при изменении статуса правила');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApiArchiveToggle = async (ruleId: number, archive: boolean) => {
    try {
      setLoading(true);

      await adsRulesApi.updateRuleArchive(ruleId, { is_archieved: archive });

      message.success(archive ? 'Правило перемещено в архив' : 'Правило восстановлено из архива');

      fetchRules(activeTab === 'rules-archived');
    } catch (error: any) {
      console.error('Error updating rule archive status:', error);
      if (error.response?.status === 400) {
        message.error('Неверный ID правила или параметр архивирования');
      } else if (error.response?.status === 401) {
        message.error('Ошибка авторизации');
      } else if (error.response?.status === 403) {
        message.error('Недостаточно прав для архивирования');
      } else if (error.response?.status === 404) {
        message.error('Правило не найдено');
      } else {
        message.error('Ошибка при архивировании правила');
      }
    } finally {
      setLoading(false);
    }
  };

  const onCloseHelpDrawer = () => {
    setHelpDrawerVisible(false);
  };

  const openHelpLink = () => {
    window.open('https://doc.scananalytics.ru/reklama/#64-оптимизатор-бюджета', '_blank');
  };

  useEffect(() => {
    const drawerBody = document.querySelector('.ant-drawer-body');
    if (drawerBody) {
      (drawerBody as HTMLElement).style.padding = '0';
    }
    function postMessageToIframe(data: any) {
      const iframe = iframeRef.current;
      if (iframe && (iframe as HTMLIFrameElement).contentWindow) {
        (iframe as HTMLIFrameElement).contentWindow!.postMessage(data, 'https://doc.scananalytics.ru');
      }
    }
    if (helpDrawerVisible) {
      postMessageToIframe('deleteElement');
    }
  });

  return (
    <MainLayout pageTitle="Управление рекламой">
      <div className="page-wrapper">
          <style>{`
          .scope-toggle {
            display: inline-flex;
            border-radius: 8px;
          }
          .scope-btn {
            background: #fff;
            color: #000;
            border: 1px solid #d9d9d9;
            padding: 6px 14px;
            line-height: 1.2;
            cursor: pointer;
            user-select: none;
            transition: all 0.2s ease;
            font-size: 14px;
          }
          .scope-btn + .scope-btn {
            margin-left: -1px;
          }
          .scope-btn.is-first {
            border-radius: 8px 0 0 8px;
          }
          .scope-btn.is-last {
            border-radius: 0 8px 8px 0;
          }
          .scope-btn.is-active {
            background: #1677ff;
            color: #fff;
            border-color: #1677ff;
            position: relative;
            z-index: 1;
          }
          .scope-btn:not(.is-active):hover {
            color: #1677ff;
            background: #f0f8ff;
            border-color: #1677ff;
          }
          .scope-btn:focus-visible {
            outline: 2px solid #1677ff;
            outline-offset: 1px;
          }
          .scope-btn:disabled {
            background: #fafafa;
            color: #bfbfbf;
            border-color: #e5e5e5;
            cursor: not-allowed;
          }
          .scope-btn:disabled:hover {
            background: #fafafa;
            color: #bfbfbf;
            border-color: #e5e5e5;
          }
          .adaptive-cascader-popup .ant-cascader-menu {
            max-height: none !important;
            height: auto !important;
          }
          .adaptive-cascader-popup .ant-cascader-menus {
            max-height: none !important;
            height: auto !important;
          }
          /* Компактный свитчер */
          .ant-switch-small {
            min-width: 24px !important;
            width: 24px !important;
            height: 14px !important;
          }
          .ant-switch-small .ant-switch-handle {
            width: 10px !important;
            height: 10px !important;
          }
          .ant-switch-small.ant-switch-checked .ant-switch-handle {
            left: calc(100% - 10px - 2px) !important;
          }
          `}</style>

          <div style={{ padding: '15px 24px 0' }}>
            <div style={{ marginBottom: 24 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16
              }}>
                <Title data-help-id="ads-rules-title" level={1} style={{ margin: 0, color: '#000', fontSize: '23px', fontWeight: '600' }}>
                  Оптимизатор рекламного бюджета
                </Title>
                <div
                  data-help-id="ads-rules-help"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer'
                  }}
                  onClick={() => setHelpDrawerVisible(true)}
                >
                  <div style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: '#ff4d4f',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    ?
                  </div>
                  <Text style={{
                    color: '#ff4d4f',
                    fontSize: '14px'
                  }}>
                    Как настроить управление рекламой
                  </Text>
                </div>
              </div>
            </div>
            <Button
              data-help-id="ads-rules-add"
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddRule}
              style={{
                backgroundColor: '#ff4d4f',
                borderColor: '#ff4d4f',
                borderRadius: '5px',
                color: '#fff'
              }}
            >
              Добавить правило
            </Button>
          </div>
          <div className="page-content">
            {
              modalOpen && <RuleFormModal
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onSave={handleSaveRule}
                editingRule={editingRule}
                initialFormData={state}
                actionTypes={actionTypes || undefined}
              />
            }
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              size="large"
              items={[
                { key: 'rules-active', label: 'Действующие правила' },
                { key: 'rules-archived', label: 'Архивные' },
                { key: 'history', label: 'История выполнения' },
              ]}
              style={{ marginBottom: 16 }}
            />

            <div>
              {(activeTab === 'rules-active' || activeTab === 'rules-archived') && (
                <RulesTable
                  rules={savedRules}
                  apiRules={apiRules}
                  loading={loading}
                  pagination={pagination}
                  onEdit={handleEditRule}
                  onApiEdit={handleEditApiRule}
                  onDelete={handleDeleteRule}
                  onUnarchive={handleUnarchiveRule}
                  onToggleStatus={handleToggleStatus}
                  onAdd={handleAddRule}
                  onDuplicate={handleDuplicateRule}
                  onApiDuplicate={handleDuplicateApiRule}
                  onFiltersChange={handleFiltersChange}
                  onTableChange={handleTableChange}
                  scopeOne={scopeOne}
                  activeTab={activeTab === 'rules-active' ? 'active' : 'archived'}
                  actionTypes={actionTypes || undefined}
                  dictionaries={dictionaries || undefined}
                  onApiStatusToggle={handleApiStatusToggle}
                  onApiArchiveToggle={handleApiArchiveToggle}
                  sorting={sorting}
                />
              )}
              {activeTab === 'history' && (
                <ExecutionHistoryTable
                  history={executionHistory}
                  apiHistory={apiHistory}
                  loading={historyLoading}
                  pagination={historyPagination}
                  dictionaries={dictionaries || undefined}
                  onFiltersChange={handleHistoryFiltersChange}
                  onPageChange={(page, pageSize) => fetchHistory(undefined, page, pageSize)}
                />
              )}
            </div>
          </div>
          <Drawer
            placement="right"
            onClose={onCloseHelpDrawer}
            open={helpDrawerVisible}
            width={650}
            closable={false}
            rootStyle={{ padding: 0, overflow: 'hidden' }}
          >
            <div style={{ position: 'absolute', top: 30, left: -40, zIndex: 1000 }}>
              <Button
                icon={<CloseOutlined />}
                onClick={onCloseHelpDrawer}
                shape="circle"
                style={{ display: 'block', marginBottom: 10 }}
              />
              <Button
                icon={<LinkOutlined />}
                onClick={openHelpLink}
                shape="circle"
                style={{ display: 'block' }}
              />
            </div>
            <div style={{ marginTop: '10px' }}></div>
            <iframe
              className="help-iframe"
              ref={iframeRef}
              src={`https://doc.scananalytics.ru/reklama/?timestamp=${new Date().getTime()}#64-%D0%BE%D0%BF%D1%82%D0%B8%D0%BC%D0%B8%D0%B7%D0%B0%D1%82%D0%BE%D1%80-%D0%B1%D1%8E%D0%B4%D0%B6%D0%B5%D1%82%D0%B0`}
            />
          </Drawer>
      </div>
    </MainLayout>
  );
}

export default AdsRules;